import { AGENT_FULLPAGE_ACTIVE_THREAD_KEY, AGENT_SESSION_FRESHNESS_MS } from './agent.types';

// Single source of truth for "which conversation fullscreen /agent opens to". Both the
// fullpage layout and the bottom panel write here, so the chat you last focused — in
// either surface — is the one fullscreen restores. Stored as { id, ts }: the timestamp
// drives the shared session-freshness window, so a stale entry is treated as absent and
// we start fresh after a long idle gap rather than reopening an expensive, uncached old
// thread. The storage key is shared via agent.types so closing a tab can clear it.

interface PersistedActiveThread {
  id: string;
  ts: number;
}

// Restore the last-open conversation if it's still within the freshness window. Returns
// null when nothing is stored, the entry is malformed, or it has gone stale.
export function loadLastOpenThreadId(): string | null {

  try {
    const raw = localStorage.getItem(AGENT_FULLPAGE_ACTIVE_THREAD_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedActiveThread>;
    if (!parsed.id || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > AGENT_SESSION_FRESHNESS_MS) return null;
    return parsed.id;
  } catch {
    return null;
  }
}

// Record (or, with null, forget) the conversation fullscreen should open to, stamping the
// current time to renew the freshness window. Writing null is how an explicit New Chat is
// remembered as a New Chat.
export function writeLastOpenThreadId(threadId: string | null): void {

  try {
    if (threadId) {
      const state: PersistedActiveThread = { id: threadId, ts: Date.now() };
      localStorage.setItem(AGENT_FULLPAGE_ACTIVE_THREAD_KEY, JSON.stringify(state));
    } else {
      localStorage.removeItem(AGENT_FULLPAGE_ACTIVE_THREAD_KEY);
    }
  } catch {
    // Storage unavailable (quota / private mode) — skip persistence.
  }
}

// Forget the last-open conversation only if it currently points at this thread, so
// closing one chat never wipes the memory of a different last-open conversation. Ignores
// the freshness window deliberately — a closed chat should be dropped regardless of age.
export function clearLastOpenThreadIdIfMatches(threadId: string): void {

  try {
    const raw = localStorage.getItem(AGENT_FULLPAGE_ACTIVE_THREAD_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { id?: string };
    if (parsed?.id === threadId) localStorage.removeItem(AGENT_FULLPAGE_ACTIVE_THREAD_KEY);
  } catch {
    // Storage unavailable or malformed — nothing to clear.
  }
}
